import { action, observable, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import OrderStore from './OrderStore';
import { OrderLineStore } from 'modules/order-line/stores';
import { SyncStore } from 'modules/sync/stores';
import { notificationError, formatDates, isSuperAdminOrganization, isShippingStateAvailable, buildAddressValidationErrors } from 'core/utils';
import {
	orderStatus,
	orderLineStatuses,
	orderLineStatusesForSplit,
	sourceOfChange,
	fulfillmentOrderStatus,
	channelType,
} from 'common/constants';
import { ModalParams } from 'core/models';
import _ from 'lodash';
import { MarketplaceOrderOnHold, Replacement } from '../pages';
import Cancel from '../pages/Cancel';
import { CancelUncancelResult } from '../pages';
import { isSome } from 'core/utils';
import { getAddressValidationErrorsTooltipData } from 'core/utils/addressValidationInfoUtils';

class MarketplaceOrderDetailViewStore extends BaseViewStore {
	@observable data = [];
	@observable refresh = false;
	@observable orderLines = null;
	@observable fulfillmentOrders = null;
	@observable isReadyForSplitting = false;
	@observable shipmentInfoReturns = [];
	@observable addressValidationErrorsTooltipData = '';
	itemPrice = 0;
	hasPiiAccess = false;
	isBuyerRequestedCancel = false;
	order = null;
	showSplitOption;
	hasUnacknowledgedOrderLines = false;

	historyLogModal = new ModalParams({
		onClose: this.closeHistoryLogModal,
	});

	getCancelOrderConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.cancelAllOrderLines(),
			message: ['ORDERVIEW.CANCELALLORDERLINESCONFIRM'],
		};
	};

	getRefundConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.refundAllOrderLines(),
			message: ['ORDERVIEW.REFUNDALLORDERLINESCONFIRM'],
		};
	};

	getAcknowledgeOrderConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.acknowledgeOrder(),
			message: ['ORDERVIEW.ACKNOWLEDGEORDERCONFIRM'],
		};
	};

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.listStore = new OrderStore(rootStore);
		this.orderStore = new OrderStore(rootStore);
		this.orderLineStore = new OrderLineStore(rootStore);
		this.syncStore = new SyncStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound async initialize() {
		this.suspend();
		await this.getOrder(this.rootStore.routerStore.routerState.params.id, [
			'shippingCoreCountry',
			'shippingCoreCountryState',
			'fulfillmentOrders',
			'fulfillmentOrders.fulfillmentOrderStatus',
			'fulfillmentOrders.carrierService',
			'fulfillmentOrders.carrierService.carrierType',
			'fulfillmentOrders.onHoldReason',
			'fulfillmentOrders.warehouseOrganization',
			'fulfillmentOrders.shipmentInfos',
			'fulfillmentOrders.fulfillmentOrderItems',
			'shipmentInfos',
			'shipmentInfos.returnReason',
			'shipmentInfos.returnedItems',
			'shipmentInfos.returnedItems.product',
			'shipmentInfos.carrierService',
			'shipmentInfos.carrierService.carrierType',
			'carrier',
			'channel',
			'channel.timezone',
			'channel.channelType',
			'confirmationType',
			'currency',
			'seller',
			'seller.sellerOrganizationInfo',
			'orderStatus',
			'cancelReason',
			'orderType',
			'orderLines',
			'orderLines.product',
			'orderLines.product.productUpcs',
			'orderLines.orderLineStatus',
			'shippingCoreCountry',
			'shippingCoreCountryState',
			'onHoldReason',
			'requiredCarrier'
		]);
		if (this.order.addressValidationErrors) {
			this.addressValidationErrorsTooltipData = this.order.addressValidationErrors.split(',');
		}
		this.resume();
	}

	@action.bound
	getOrder = async (id, options) => {
		this.orders = await this.listStore.findOrder({
			embed: options,
			ids: [id],
			fetchActiveShipments: true,
		});

		this.order = this.orders.item[0];
		this.orderLines = this.order.orderLines;
		_.forEach(this.orderLines, orderLine => {
			if (!orderLine.orderLineStatusId) orderLine.orderLineStatus = { name: '-' };
		});
		this.fulfillmentOrders = this.order.fulfillmentOrders.map(fo => {
			return { ...fo, orderChannel: _.get(this.order, 'channel.name') };
		});
		this.shipmentInfoReturns = this.order.shipmentInfos;
		this.isReadyForSplitting =
			this.order.orderStatus.abrv === orderStatus.unshipped ||
			this.order.orderStatus.abrv === orderStatus.partiallyShipped;

		if (
			!this.order.seller.sellerOrganizationInfo.partiallyFulfillMixedOrders &&
			this.orderLines.some(ol => ol.product != null && !ol.product.isActive) &&
			this.fulfillmentOrders.length == 0
		) {
			this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list');
			this.rootStore.notificationStore.warning([
				'ORDERVIEW.PARTIALLYFULFILLMIXEDORDERS',
				{ orderId: this.order.orderNumber },
			]);
			this.resume();
			return;
		}

		formatDates(this.order, ['orderDate', 'paymentDate'], false, 'LLL');
		formatDates(this.order, ['shipByDate', 'deliverByDate'], false, 'L');
		this.hasUnlinkedOrderLines =
			this.orderLines &&
			this.orderLines.length != 0 &&
			_.find(this.orderLines, item => {
				return item.product == null;
			});

		// Some of conditions for rendering cancel and refund buttons.
		if (
			this.order.channel.channelType.name == channelType.amazon ||
			this.order.channel.channelType.name == channelType.mirakl
		) {
			this.isBuyerRequestedCancel =
				this.orderLines != null && this.orderLines.length != 0
					? _.some(this.orderLines, item => {
							return item.isBuyerRequestedCancel == true;
					  })
					: false; // Cancelable if some of order lines are buyer requested to cancel.
			this.hasNoShipmentInfos = _.every(this.fulfillmentOrders, item => {
				return item.shipmentInfos == null || item.shipmentInfos.length == 0;
			}); // Cancelable if none of FOs have label.

			this.hasAllFOsPastExitScan =
				this.fulfillmentOrders != null && this.fulfillmentOrders.length != 0
					? _.every(this.fulfillmentOrders, item => {
							return (
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan ||
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.replaced ||
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.notReceived ||
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.returned
							);
					  })
					: false; // Refundable if all statuses for all FOs are past Exit Scan.
		}
		this.hasState =
			this.order.shippingCoreCountryState ||
			(this.order.shippingCoreCountry && !isShippingStateAvailable(this.order.shippingCoreCountry.countryCode));
		this.hasFulfillmentOrders = this.fulfillmentOrders && this.fulfillmentOrders.length != 0 ? true : false;
		// Same rule as the split job: a line counts as split while any FO, canceled ones included, holds an item of it.
		const splitOrderLineIds = new Set(
			_.flatMap(this.fulfillmentOrders, fo => fo.fulfillmentOrderItems || []).map(item => item.orderLineId)
		);
		this.showSplitOption =
			!!this.order.shippingCoreCountry &&
			this.order.orderLines.some(ol => {
				return (
					ol.product &&
					ol.product.isShippable &&
					orderLineStatusesForSplit.includes(ol.orderLineStatus.abrv) &&
					!splitOrderLineIds.has(ol.id)
				);
			});
		this.hasUnacknowledgedOrderLines = this.order.orderLines.some(
			ol => ol.orderLineStatus.abrv === orderLineStatuses.created
		);

		this.calculateItemPrice();
	};

	@action.bound
	cancelAllOrderLines = async () => {
		this.suspend();
		try {
			const {
				data: { total, canceled, refunded, message },
			} = await this.cancelAllOrderLinesOnChannel();
			if (message) {
				this.rootStore.notificationStore.error(message); //// CANCELEDORDERTOOLATE
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.CANCELORDERSSUCCESS',
					{ total, canceled, refunded, failed: total - (canceled + refunded) },
				]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	cancelAllOrderLinesOnChannel = () => {
		switch (this.order.channel.channelType.name) {
			case channelType.amazon:
				return this.orderLineStore.cancelAmazonOrder(this.order.id, sourceOfChange.cancelAllOrderLines);
			case channelType.walmart:
				// No order line ids means every line on the order.
				return this.orderLineStore.cancelWalmartOrderLines(
					this.order.id,
					[],
					sourceOfChange.cancelWalmartOrderLines
				);
			default:
				return this.orderLineStore.cancelMiraklOrder(this.order.id, sourceOfChange.cancelAllOrderLines);
		}
	};

	@action.bound
	acknowledgeOrder = async () => {
		this.suspend();
		let acknowledged = false;
		try {
			await this.syncStore.acknowledgeWalmartOrder(this.order.id, sourceOfChange.acknowledgeWalmartOrder);
			this.rootStore.notificationStore.success(['ORDERVIEW.ACKNOWLEDGEORDERSUCCESS']);
			acknowledged = true;
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
		if (acknowledged) {
			// Walmart flips the order line statuses to Acknowledged, so reload to show them.
			this.refreshPage();
		}
	};

	@action.bound
	refundAllOrderLines = async () => {
		this.suspend();
		try {
			const {
				data: { total, canceled, refunded, message },
			} = await this.orderLineStore.refundAllOrderLines(this.order.id, sourceOfChange.cancelAllOrderLines);
			if (message) {
				this.rootStore.notificationStore.error(message);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.REFUNDORDERLINESSUCCESS',
					{ total, refunded, failed: total - (canceled + refunded) },
				]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	calculateItemPrice() {
		let itemPrice = 0;
		_.each(this.order.orderLines, orderLine => {
			itemPrice += orderLine.unitPrice * orderLine.quantity;
		});
		this.itemPrice = itemPrice.toFixed(2);
	}

	@action.bound
	splitOrder = async () => {
		this.showSplitOption = false;
		this.suspend();
		try {
			var response = await this.listStore.splitMarketplaceOrder(
				this.rootStore.routerStore.routerState.params.id,
				sourceOfChange.splitOrder
			);
			if (response[0].isSuccess) {
				response[0].inactiveOrderLineIds.length === 0
					? this.rootStore.notificationStore.success(['ORDERVIEW.SPLITMARKETPLACEORDERSUCCESS'])
					: this.rootStore.notificationStore.warning([
							'ORDERVIEW.SPLITMARKETPLACEORDERPARTIALSUCCESS',
							{
								successful: this.orderLines.length - response[0].inactiveOrderLineIds.length,
								total: this.orderLines.length,
							},
					  ]);
			} else {
				this.rootStore.notificationStore.error([
					response[0].message ? response[0].message : 'ORDERVIEW.ORDERINQUEUEFORSPLIT',
				]);
			}
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.initialize();
		}
		this.resume();
	};

	goBack = () => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list', {});
	};

	refreshPage = () => {
		this.initialize();
	};

	@action.bound
	openReplacementModal = async () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.order.id, parentView: this },
			component: Replacement,
		});
	};

	@action.bound
	openOnHoldModal = async () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { id: this.order.id },
			component: MarketplaceOrderOnHold,
		});
	};

	@action.bound
	async openHistoryLogModal() {
		this.historyLogModal.open();
	}

	@action.bound
	async closeHistoryLogModal() {
		this.historyLogModal.close();
	}

	@action.bound
	openCancelMarketplaceOrderModal = async () => {
		this.openModal({
			onSuccess: async () => this.initialize(),
			data: {
				selectedItemId: this.order.id,
				isMarketplaceOrder: true,
			},
			component: Cancel,
		});
	};

	getUncancelOrderConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.uncancelMarketplaceOrder(),
			message: ['MARKETPLACEORDERDETAILVIEW.UNCANCELORDERCONFIRMMESSAGE', { orderNumber: this.order.orderNumber }],
		};
	};

	@action.bound
	uncancelMarketplaceOrder = async () => {
		try {
			this.suspend();
			const response = await this.orderStore.uncancelMarketplaceOrder(
				{ orderId: this.order.id },
				sourceOfChange.uncancelMarketplaceOrder
			);
			if (response.result == 'success') {
				this.rootStore.notificationStore.success('Order uncanceled successfully. Click for more details.', false, {
					autoClose: true,
					onClick: () => {
						this.openModal({
							data: response,
							component: CancelUncancelResult,
						});
					},
				});
			} else if (response.result == 'partial') {
				this.rootStore.notificationStore.warning('Order partially uncanceled. Click for more details.', false, {
					autoClose: false,
					onClick: () => {
						this.openModal({
							data: response,
							component: CancelUncancelResult,
						});
					},
				});
			} else {
				this.rootStore.notificationStore.error('Order uncancel failed. Click for more details.', false, {
					autoClose: false,
					onClick: () => {
						this.openModal({
							data: response,
							component: CancelUncancelResult,
						});
					},
				});
			}
			await this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.resume();
		}
	};
}

export default MarketplaceOrderDetailViewStore;
